// src/services/regionsService.ts
//
// All Supabase access for the yak_regions table (region hierarchy) is
// isolated here. Region CRUD is admin config: direct table writes under
// RLS (spine AD-3) — the SELECT policy covers all org members, the write
// policies require an admin role. Components/hooks consume the clean
// `src/types/yak.ts` types only; results are cast once per method.

import { supabase } from '../lib/supabase'
import type { YakRegion } from '../types/yak'

// The DB enforces the region invariants (delete guards, name uniqueness,
// cycle rejection); these map its error codes to stable human copy. On
// delete, 23503 deliberately covers ANY referencing table so the message
// stays correct as later stories add reps/assignments/ticket references.
// On write, the mapped codes are the ones reachable under concurrent edits
// (another admin deleted the row or its new parent between load and save) —
// without them the raw Postgres string would leak to the user.
const DELETE_BLOCKED_MESSAGE =
  "This region can't be deleted because other records still reference it"
const DUPLICATE_NAME_MESSAGE =
  'A region with this name already exists in your organization'
const PARENT_MISSING_MESSAGE =
  'That parent region no longer exists — refresh and try again'
const CYCLE_MESSAGE =
  "A region can't be moved under itself or one of its own descendants"
const STALE_ROW_MESSAGE =
  'This region no longer exists — it may have been changed by someone else. Refresh and try again'
const EMPTY_NAME_MESSAGE = 'Region name cannot be empty'

interface PostgrestErrorLike {
  message: string
  code?: string
}

// Exported for unit testing — the mapping is the concurrency-safety contract.
export function throwMapped(
  error: PostgrestErrorLike,
  action: 'write' | 'delete'
) {
  if (action === 'delete') {
    if (error.code === '23503') throw new Error(DELETE_BLOCKED_MESSAGE)
    throw new Error(error.message)
  }
  // write = insert (create) or update (rename/move)
  if (error.code === '23505') throw new Error(DUPLICATE_NAME_MESSAGE)
  if (error.code === '23503') throw new Error(PARENT_MISSING_MESSAGE)
  if (error.code === '23514') throw new Error(CYCLE_MESSAGE) // cycle-guard trigger
  if (error.code === 'PGRST116') throw new Error(STALE_ROW_MESSAGE) // 0 rows updated
  throw new Error(error.message)
}

class RegionsService {
  // Whole org tree as flat rows (RLS scopes to the caller's org); the
  // UI builds the nested tree client-side. Ordered by name so siblings
  // render stably.
  async listRegions(): Promise<YakRegion[]> {
    const { data, error } = await supabase
      .from('yak_regions')
      .select('*')
      .order('name', { ascending: true })

    if (error) throw new Error(error.message)
    return (data as unknown as YakRegion[] | null) || []
  }

  // organization_id comes from the caller's JWT claims — a row outside
  // the caller's org would be refused by RLS anyway, but inserting needs
  // the value explicitly.
  async createRegion(
    name: string,
    parentRegionId: string | null
  ): Promise<YakRegion> {
    // Trim at the boundary so no caller (not just the UI) can persist a
    // blank or whitespace-only name — Story 1.2's by-name rep import
    // relies on names being meaningful and unambiguous.
    const trimmed = name.trim()
    if (!trimmed) throw new Error(EMPTY_NAME_MESSAGE)

    const {
      data: { session },
    } = await supabase.auth.getSession()
    const organizationId = session?.user?.app_metadata?.organization_id ?? null
    if (!organizationId) {
      throw new Error('Your account is not linked to an organization')
    }

    const { data, error } = await supabase
      .from('yak_regions')
      .insert({
        organization_id: organizationId,
        name: trimmed,
        parent_region_id: parentRegionId,
      })
      .select('*')
      .single()

    if (error) throwMapped(error, 'write')
    return data as unknown as YakRegion
  }

  async renameRegion(id: string, name: string): Promise<YakRegion> {
    const trimmed = name.trim()
    if (!trimmed) throw new Error(EMPTY_NAME_MESSAGE)

    const { data, error } = await supabase
      .from('yak_regions')
      .update({ name: trimmed })
      .eq('id', id)
      .select('*')
      .single()

    if (error) throwMapped(error, 'write')
    return data as unknown as YakRegion
  }

  // Move = one UPDATE of parent_region_id; the subtree follows in an
  // adjacency list. The DB trigger rejects moves under the node's own
  // descendants (the picker also excludes them).
  async moveRegion(
    id: string,
    newParentRegionId: string | null
  ): Promise<YakRegion> {
    const { data, error } = await supabase
      .from('yak_regions')
      .update({ parent_region_id: newParentRegionId })
      .eq('id', id)
      .select('*')
      .single()

    if (error) throwMapped(error, 'write')
    return data as unknown as YakRegion
  }

  async deleteRegion(id: string): Promise<void> {
    const { error } = await supabase.from('yak_regions').delete().eq('id', id)

    if (error) throwMapped(error, 'delete')
  }
}

export const regionsService = new RegionsService()
